'use client';
import React from 'react';
import Navbar from '@/components/Navbar';
import {
  ShieldCheck,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  Award,
  Compass,
  Users,
} from 'lucide-react';
import Footer from '@/components/Footer';
import { useSiteSettings } from '@/components/SiteSettingsProvider';

export default function AboutUsPage() {
  const { phoneNumber, showPhoneNumber } = useSiteSettings();
  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800 flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-[120px] pb-20 bg-slate-950 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 z-0" />
        <div className="absolute top-[-100px] right-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl pointer-events-none z-0" />

        <div className="relative z-10 max-w-[1170px] mx-auto px-6 w-full text-left">
          <div
            style={{ height: '30px' }}
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 w-fit backdrop-blur-md"
          >
            WHO WE ARE
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-none mb-4">
            About{' '}
            <span className="bg-gradient-to-r from-accent via-accent-light to-blue-500 bg-clip-text text-transparent">
              Faremaps
            </span>
          </h1>
          <p className="text-slate-400 text-xs md:text-sm font-semibold max-w-2xl leading-relaxed">
            Discover our journey of creating endless travel opportunities, securing wholesale
            private airline rates, and making dream destinations reachable.
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-grow max-w-[1170px] mx-auto px-6 py-16 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Main text column */}
          <div className="lg:col-span-8 text-left space-y-8 animate-fadeInUp">
            {/* Core Narrative */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm">
              <h2 className="text-slate-800 text-xl font-black uppercase tracking-wider mb-6 flex items-center gap-3">
                <Compass className="w-6 h-6 text-accent" />
                Our Travel Mission
              </h2>
              <p className="text-slate-600 text-sm font-medium leading-relaxed mb-6">
                Faremaps is a progressively developing online travel agency dealing in international
                and omnichannel travel arrangements. We offer a wide range of travel products and
                services that inspire the traveler to gain the convenience of flight booking, shop
                for cheap airfares, and create endless opportunities for traveling.
              </p>
              <p className="text-slate-600 text-sm font-medium leading-relaxed">
                At Faremaps, we are leaping to a new way of journeying the world. Travelers have the
                opportunity to find well-searched deals on flight tickets, last-minute flight deals,
                and exclusive deals on the holidays.
              </p>
            </div>

            {/* Value props */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm">
                <div className="w-12 h-12 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent mb-4">
                  <Award className="w-6 h-6" />
                </div>
                <h3 className="text-slate-800 text-sm font-black uppercase tracking-wider mb-2">
                  All-Inclusive Air Fare Deals
                </h3>
                <p className="text-slate-500 text-xs font-semibold leading-relaxed">
                  Faremaps is a lucrative platform offering all-inclusive deals on international
                  flights with slashed prices of flight tickets, along with tailor-made trips. We
                  give our customers the ease of booking scheduled flights, group tours, diversified
                  businesses, and individuals. Customers can book cheap airfares flights to fulfill
                  their romantic desires or fly to adventurous destinations on economized flights.
                </p>
              </div>

              <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm">
                <div className="w-12 h-12 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent mb-4">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-slate-800 text-sm font-black uppercase tracking-wider mb-2">
                  Personalized Flight Planning
                </h3>
                <p className="text-slate-500 text-xs font-semibold leading-relaxed">
                  Planning an incredible trip of your life with Faremaps is definitely a
                  money-saving option, plus an insightful experience. We have exclusive flight
                  tickets and deals designed to meet your aspirations while keeping everything
                  within a budget. Each time you book your flight to any destination across the
                  globe, you earn discounts. Our specialized travel desk agents will book your
                  flight designed around your specific travel requirements.
                </p>
              </div>
            </div>

            {/* International Trips Card */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm">
              <h3 className="text-slate-800 text-sm font-black uppercase tracking-wider mb-4">
                International Trips Stirring Marvels
              </h3>
              <p className="text-slate-600 text-sm font-medium leading-relaxed mb-4">
                Faremaps is the niche marketplace where you end up taking plenty of choices in
                travel and relish in them. We cover not only flight tickets, but also serve you well
                with hotel accommodations, transportation, and much more. Flight Trips taken on
                Faremaps is an incredible venture that leads to exploration, innovation, and
                overwhelming transit to enriching moments.
              </p>
              <p className="text-slate-600 text-sm font-medium leading-relaxed">
                Choose the best travel deals and lead your way towards a unique kind of
                self-realization, and above all, you are going to live every minute of your life.
                Look out for the best travel deals and enjoy the comfort of flying in explorative
                worlds, where imagination takes an oblique string.
              </p>
            </div>
          </div>

          {/* Sticky Sidebar */}
          <div className="lg:col-span-4 sticky top-[110px] space-y-6">
            {/* VIP Dedicated Desk Card */}
            {showPhoneNumber && (
              <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm text-left space-y-6">
                <div>
                  <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                    VIP Booking Desk
                  </h4>
                  <p className="text-slate-500 text-xs font-semibold leading-relaxed mb-4">
                    Speak with our expert consultants for instant phone-only private flight deals.
                  </p>

                  <a
                    href={`tel:${phoneNumber}`}
                    className="flex items-center gap-3.5 bg-slate-950 hover:bg-slate-900 border border-white/10 rounded-2xl p-4 shadow-premium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
                  >
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-blue-600 shadow-glow">
                      <span className="absolute inset-0 rounded-xl bg-accent animate-ping opacity-20"></span>
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                        TOLL-FREE 24/7
                      </div>
                      <div className="text-sm font-black text-white mt-1 tracking-tight leading-none group-hover:text-accent-light transition-colors">
                        {phoneNumber}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}

              <div>
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                  Secure Ticketing
                </h4>
                <div className="flex items-start gap-3 bg-slate-50 border border-slate-200/60 p-4 rounded-2xl">
                  <ShieldCheck className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-slate-800 text-xs font-bold leading-tight">
                      100% Verified Reservations
                    </h5>
                    <p className="text-slate-400 text-[10px] font-semibold mt-1 leading-normal">
                      Every flight transaction is PCI-compliant and directly validated by global
                      airlines.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-3 block">
                  Corporate Office
                </h4>
                <div className="space-y-3">
                  <div className="flex gap-2.5 items-start text-xs text-slate-500 font-bold">
                    <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>13900 NE 12th St Bellevue, WA 98005, USA</span>
                  </div>
                  <div className="flex gap-2.5 items-center text-xs text-slate-500 font-bold">
                    <Mail className="w-4 h-4 text-accent flex-shrink-0" />
                    <a
                      href="mailto:support@faremaps.com"
                      className="hover:text-accent transition-colors"
                    >
                      support@faremaps.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
